import React, { useEffect, useRef, useState } from 'react'
import type { GiftDef } from '../../hooks/useGifts'

type GiftDrawerProps = {
  open: boolean
  onClose: () => void
  creatorName?: string
  avatarUrl?: string
  catalog: GiftDef[]
  onSend: (args: { gift: GiftDef }) => void
}

export default function GiftDrawer({ open, onClose, creatorName, avatarUrl, catalog, onSend }: GiftDrawerProps) {
  const [mounted, setMounted] = useState(open)
  const closeTimer = useRef<number | null>(null)

  useEffect(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    if (open) {
      setMounted(true)
      return
    }
    // Keep mounted briefly so the slide-out transition can run.
    closeTimer.current = window.setTimeout(() => setMounted(false), 220)
    return () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current)
    }
  }, [open])

  if (!mounted) return null

  return (
    <div>
      {/* Backdrop */}
      <div
        aria-hidden={!open}
        className={`fixed inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'} z-40`}
        onClick={onClose}
      />
      {/* Side drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        inert={!open ? '' : undefined}
        className={[
          // Mobile: bottom sheet. Desktop: right drawer.
          'fixed z-50 transform transition-transform bg-neutral-900 shadow-2xl border-white/10',
          'left-0 right-0 bottom-0 top-auto w-full h-[min(56vh,480px)] border-t rounded-t-2xl',
          'md:left-auto md:top-0 md:bottom-auto md:right-0 md:h-full md:w-[360px] md:rounded-none md:border-l md:border-t-0',
          open ? 'translate-x-0 translate-y-0' : 'translate-y-full md:translate-y-0 md:translate-x-full',
        ].join(' ')}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-neutral-900 border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-3">
            {avatarUrl ? (
              <img src={avatarUrl} className="h-8 w-8 rounded-full object-cover border border-white/20" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-white/10" />
            )}
            <div className="font-semibold truncate">
              Send a gift{creatorName ? ` to ${creatorName}` : ''}
            </div>
            <button type="button" onClick={onClose} className="ml-auto text-white/80 hover:text-white" aria-label="Close">
              X
            </button>
          </div>
        </div>

        {/* Catalog grid */}
        <div className="chat-scroll overflow-y-auto h-[calc(100%-52px)] p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
            {catalog.map((g) => (
              <button
                key={g.id}
                onClick={() => onSend({ gift: g })}
                className="rounded-xl border border-white/10 bg-neutral-800 hover:bg-neutral-700 p-3 flex flex-col items-center text-center"
              >
                <div className="text-2xl">{g.emoji}</div>
                <div className="text-sm mt-1 font-semibold truncate w-full">{g.name}</div>
                <div className="text-xs text-white/70">{g.amountLabel}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
